import React from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
} from '@patternfly/react-core';
import { DatabaseIcon, ExclamationCircleIcon, SyncAltIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useVmBackups } from '../../../hooks/useVmBackups';
import BackupTreeTable from '../Backups/BackupTreeTable';

interface VmBackupsSectionProps {
  /** The NetBox VM id the tab already receives — not the URL string. */
  vmId: number;
}

/**
 * Copies of this VM held on the backup server — conceptually **relocated snapshots**, which is
 * why they live inside the Snapshots tab rather than in a tab of their own.
 *
 * Strictly read-only: the resource has no write routes, so there is no create, delete, restore or
 * "run backup now" affordance here, not even a disabled one.
 *
 * The parent `<Tabs>` uses `mountOnEnter`, so this mounts on first open and then stays mounted
 * without refetching — hence its own Refresh button, like the snapshots section's retry.
 *
 * `staleAfterHours` is `null` here on purpose: the inventory summary that publishes the threshold
 * is a page-level call, so the tab shows the factual age and passes no staleness verdict rather
 * than hardcoding the number or firing an aggregate query per tab.
 */
const VmBackupsSection: React.FunctionComponent<VmBackupsSectionProps> = ({ vmId }) => {
  const { backups, loading, error, refetch } = useVmBackups(vmId);

  const renderBody = () => {
    if (loading && backups.length === 0) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load backups"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error.message}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <BackupTreeTable
        subjects={backups}
        staleAfterHours={null}
        ariaLabel="Backups for this virtual machine"
        emptyState={
          <EmptyState titleText="No backups found for this VM" icon={DatabaseIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>
              {/* Honest about the match rule: the join key is the VM name, matched exactly against
                  the backup directory, so a decommissioned VM's `.old` directory can never appear
                  here. Those are still on the Backups page. */}
              Backups are matched to this VM by name. A decommissioned VM&apos;s backup directory keeps its
              <code> .old</code> suffix, so it never matches a live VM — look for it on the Backups page instead.
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="link" component={(props) => <Link {...props} to="/datacenter/backups" />}>
                  Go to Backups
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        }
      />
    );
  };

  return (
    <Card isCompact className="pf-v6-u-mt-md">
      <CardHeader
        actions={{
          actions: (
            <Button
              variant="secondary"
              icon={<SyncAltIcon />}
              isLoading={loading}
              isDisabled={loading}
              spinnerAriaValueText="Refreshing backups"
              onClick={() => refetch()}
            >
              Refresh
            </Button>
          ),
        }}
      >
        <CardTitle>Backups</CardTitle>
      </CardHeader>
      <CardBody>
        <Content component="p">
          Restore points copied to the backup server. Read-only — retention is managed by Ancalagon.
        </Content>
        {renderBody()}
      </CardBody>
    </Card>
  );
};

export default VmBackupsSection;
